import React, {useEffect, useState} from 'react';
import axios from 'axios';
import MasterSelection from './MasterSelection';

const API = process.env.REACT_APP_API_BASE_URL;

const formatDateTime = value => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Lets a quotation pick a visitor (via the MasterSelection modal) and one or
 * more of their visits. On selection the backend marks those visits' lead
 * status ("Quotation Issued" on create, "Quotation Revision" on update).
 *
 * Props:
 *  - companyId: company to scope visitors to
 *  - visitor: selected visitor id (string) or null
 *  - visits: array of selected visit ids
 *  - onChange: (visitorId, visitIds) => void
 */
const VisitorVisitSelector = ({companyId, visitor, visits = [], onChange, isDarkMode = false}) => {
  const [visitorName, setVisitorName] = useState('');
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [visitorVisits, setVisitorVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Resolve the selected visitor's name for display.
  useEffect(() => {
    if (!visitor) {
      setVisitorName('');
      return undefined;
    }
    let active = true;
    axios
      .get(`${API}/visitors/${visitor}`)
      .then(res => {
        if (active) setVisitorName(res.data?.data?.name || '');
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [visitor]);

  // Load the visitor's visits whenever the selected visitor changes.
  useEffect(() => {
    if (!visitor) {
      setVisitorVisits([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/visits/visitor/${visitor}`)
      .then(res => {
        if (active) setVisitorVisits(res.data?.data || []);
      })
      .catch(err => console.error('Error loading visits:', err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [visitor]);

  const toggleVisit = visitId => {
    const next = visits.includes(visitId)
      ? visits.filter(id => id !== visitId)
      : [...visits, visitId];
    onChange(visitor, next);
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <svg className={`w-4 h-4 ${isDarkMode ? 'text-cyan-300' : 'text-cyan-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-700'}`}>Visitor</span>
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-500 border border-gray-200'}`}>
            Optional
          </span>
        </div>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Link this quotation to a visitor and mark the visits it came from.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowVisitorModal(true)}
            className={`flex-1 flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm text-left transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 ${
              isDarkMode
                ? 'bg-gray-800 border-gray-600 hover:border-cyan-400 hover:bg-gray-700'
                : 'bg-white border-gray-300 hover:border-cyan-400 hover:bg-gray-50'
            }`}
          >
            <span className={`truncate ${visitor ? (isDarkMode ? 'text-gray-100' : 'text-gray-900') : (isDarkMode ? 'text-gray-400' : 'text-gray-500')}`}>
              {visitorName || (visitor ? 'Selected visitor' : 'Select visitor')}
            </span>
            <svg className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {visitor ? (
            <button
              type="button"
              onClick={() => {
                setVisitorName('');
                onChange(null, []);
              }}
              className={`px-3 rounded-xl border text-sm font-medium transition-colors ${
                isDarkMode
                  ? 'border-gray-600 text-gray-300 hover:text-red-300 hover:border-red-500/60'
                  : 'border-gray-300 text-gray-500 hover:text-red-500 hover:border-red-300'
              }`}
            >
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {visitor ? (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-700'}`}>Visits</span>
            <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{visits.length} selected</span>
          </div>
          {loading ? (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>Loading visits...</p>
          ) : visitorVisits.length === 0 ? (
            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              No visits recorded for this visitor.
            </p>
          ) : (
            <div className={`max-h-56 overflow-y-auto custom-scrollbar rounded-xl border divide-y ${
              isDarkMode ? 'border-gray-700 divide-gray-700 bg-gray-800' : 'border-gray-200 divide-gray-100 bg-white'
            }`}>
              {visitorVisits.map(visit => (
                <label
                  key={visit._id}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-cyan-50/60'
                  } ${visits.includes(visit._id) ? (isDarkMode ? 'bg-cyan-500/10' : 'bg-cyan-50') : ''}`}>
                  <input
                    type="checkbox"
                    checked={visits.includes(visit._id)}
                    onChange={() => toggleVisit(visit._id)}
                    className="w-4 h-4 rounded accent-cyan-600"
                  />
                  <span className={`flex-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDateTime(visit.visitedAt)} ·{' '}
                    <span className={`font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{visit.response}</span> ·{' '}
                    <span className={isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}>{visit.leadStatus}</span>
                    {visit.itemsOfInterest?.length
                      ? ` · ${visit.itemsOfInterest.join(', ')}`
                      : ''}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <MasterSelection
        isOpen={showVisitorModal}
        onClose={() => setShowVisitorModal(false)}
        onSelect={item => {
          setVisitorName(item.name || '');
          onChange(item._id, []);
          setShowVisitorModal(false);
        }}
        allowedTypes={['Visitor']}
        defaultType="Visitor"
        title="Select Visitor"
      />
    </div>
  );
};

export default VisitorVisitSelector;
